/** Firestore document shapes for billing, plus their normalizers. */

import { asInt, asNullableInt, asRecord, asText, toMillis } from './docValues';
import type { EntitlementSource, FeatureOverride } from './entitlements';
import {
  BILLING_FEATURES,
  PLAN_CATALOG,
  TRIAL_DAYS,
  isPlanId,
  type BillingFeature,
  type PlanId,
} from './plans';
import { PLATFORM_FEE_BPS } from './platformFee';
import {
  DEFAULT_DUNNING_POLICY,
  isSubscriptionStatus,
  type DunningPolicy,
  type SubscriptionStatus,
} from './subscriptionStatus';

export const BILLING_RAILS = ['stripe', 'crypto', 'manual'] as const;
export type BillingRail = typeof BILLING_RAILS[number];

export const PAYMENT_METHOD_STATUSES = ['none', 'pending', 'verified', 'failed'] as const;
export type PaymentMethodStatus = typeof PAYMENT_METHOD_STATUSES[number];

export const VERIFICATION_HOLD_MODES = ['first_invoice', 'fixed', 'zero'] as const;
export type VerificationHoldMode = typeof VERIFICATION_HOLD_MODES[number];

export interface PaymentMethodRef {
  status: PaymentMethodStatus;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  verifiedAtMs: number | null;
  failureCode: string | null;
  holdAmountMinor: number | null;
  holdIntentId: string | null;
}

/** Connect account state, mirrored from `account.updated` webhooks. */
export interface ConnectAccountRef {
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  /** Requirement ids Stripe still needs before the account can charge. */
  requirementsDue: string[];
}

export interface BillingCustomerDoc {
  bankerUid: string;
  stripeCustomerId: string | null;
  stripeConnectAccountId: string | null;
  connect: ConnectAccountRef;
  founder: boolean;
  defaultRail: BillingRail;
  /** Provider id of the card kept on file for off-session charges. */
  defaultPaymentMethodId: string | null;
  creditMinor: number;
  paymentMethod: PaymentMethodRef;
}

export interface SubscriptionDoc {
  bankerUid: string;
  planId: PlanId;
  status: SubscriptionStatus;
  rail: BillingRail;
  trialEndsAtMs: number | null;
  currentPeriodStartMs: number | null;
  currentPeriodEndMs: number | null;
  cancelAtPeriodEnd: boolean;
  /** Plan that takes effect at the end of the period (scheduled downgrade). */
  pendingPlanId: PlanId | null;
  houseQuota: number | null;
  seatQuota: number | null;
  /** God-granted features outside the plan, with optional expiry. */
  overrides: FeatureOverride[];
  /** God waived billing for this banker: full access, never invoiced. */
  exempt: boolean;
  extraHouses: number;
  /** Active coupon attached at checkout / renewal. */
  promoId: string | null;
  promoCode: string | null;
  /**
   * Remaining periods that still get the discount after the first charge.
   * `null` = forever; `0` = exhausted / once already applied.
   */
  promoRemainingMonths: number | null;
  stripeSubscriptionId: string | null;
  pastDueSinceMs: number | null;
  amountDueMinor: number;
  attemptCount: number;
  nextRetryAtMs: number | null;
  lastDunningNotifiedAtMs: number | null;
  lastRenewalNotifiedAtMs: number | null;
  /** Provider timestamp of the last webhook mirrored into this row. */
  lastProviderEventAtMs: number | null;
  archived: boolean;
  archivedAtMs: number | null;
  archivedBy: string | null;
  /**
   * Banker finished plan + payment-rail onboarding after a plan was assigned.
   * False while they still owe that first setup step.
   */
  billingSetupComplete: boolean;
  updatedAtMs: number | null;
}

/** True when a real `subscriptions/{uid}` row exists (not a synthetic default). */
export function isSubscriptionAssigned(sub: Pick<SubscriptionDoc, 'updatedAtMs'>): boolean {
  return sub.updatedAtMs != null;
}

/** Commercial overrides stored in `platform/billing`, editable by god. */
export interface BillingPlatformConfig {
  /**
   * Server-side entitlement gates. Off until every existing banker has a
   * subscription row, or the rollout would lock paid features out on deploy.
   */
  enforcement: boolean;
  prices: Partial<Record<PlanId, number>>;
  extraHousePriceMinor: number | null;
  trialDays: number;
  platformFeeBps: number;
  dunning: DunningPolicy;
  verificationHoldMode: VerificationHoldMode;
  verificationHoldFixedMinor: number;
  manualAccounts: {
    zelle: string | null;
    venmo: string | null;
    transfer: string | null;
  };
}

export const DEFAULT_BILLING_CONFIG: BillingPlatformConfig = {
  enforcement: false,
  prices: {},
  extraHousePriceMinor: null,
  trialDays: TRIAL_DAYS,
  platformFeeBps: PLATFORM_FEE_BPS,
  dunning: DEFAULT_DUNNING_POLICY,
  verificationHoldMode: 'first_invoice',
  verificationHoldFixedMinor: 100,
  manualAccounts: { zelle: null, venmo: null, transfer: null },
};

export function normalizePaymentMethod(raw: unknown): PaymentMethodRef {
  const data = asRecord(raw);
  const status = String(data.status ?? '');
  return {
    status: (PAYMENT_METHOD_STATUSES as readonly string[]).includes(status)
      ? status as PaymentMethodStatus
      : 'none',
    brand: asText(data.brand),
    last4: asText(data.last4),
    expMonth: asNullableInt(data.expMonth),
    expYear: asNullableInt(data.expYear),
    verifiedAtMs: toMillis(data.verifiedAt ?? data.verifiedAtMs),
    failureCode: asText(data.failureCode),
    holdAmountMinor: asNullableInt(data.holdAmountMinor),
    holdIntentId: asText(data.holdIntentId),
  };
}

export function normalizeOverrides(raw: unknown): FeatureOverride[] {
  if (!Array.isArray(raw)) return [];
  const out: FeatureOverride[] = [];
  for (const entry of raw) {
    const data = asRecord(entry);
    const feature = String(data.feature ?? '');
    if (!(BILLING_FEATURES as readonly string[]).includes(feature)) continue;
    out.push({
      feature: feature as BillingFeature,
      expiresAtMs: toMillis(data.expiresAtMs ?? data.expiresAt),
    });
  }
  return out;
}

export function normalizeConnectAccount(raw: unknown): ConnectAccountRef {
  const data = asRecord(raw);
  return {
    chargesEnabled: data.chargesEnabled === true,
    payoutsEnabled: data.payoutsEnabled === true,
    detailsSubmitted: data.detailsSubmitted === true,
    requirementsDue: Array.isArray(data.requirementsDue) ? data.requirementsDue.map(String) : [],
  };
}

export function normalizeBillingCustomer(bankerUid: string, raw: unknown): BillingCustomerDoc {
  const data = asRecord(raw);
  const rail = String(data.defaultRail ?? '');
  return {
    bankerUid,
    stripeCustomerId: asText(data.stripeCustomerId),
    stripeConnectAccountId: asText(data.stripeConnectAccountId),
    connect: normalizeConnectAccount(data.connect),
    founder: data.founder === true,
    defaultRail: (BILLING_RAILS as readonly string[]).includes(rail)
      ? rail as BillingRail
      : 'stripe',
    defaultPaymentMethodId: asText(data.defaultPaymentMethodId),
    creditMinor: Math.max(0, asInt(data.creditMinor)),
    paymentMethod: normalizePaymentMethod(data.paymentMethod),
  };
}

export function normalizeSubscription(bankerUid: string, raw: unknown): SubscriptionDoc {
  const data = asRecord(raw);
  const planId = isPlanId(data.planId) ? data.planId : 'basic';
  const status = isSubscriptionStatus(data.status) ? data.status : 'expired';
  const rail = String(data.rail ?? '');
  return {
    bankerUid,
    planId,
    status,
    rail: (BILLING_RAILS as readonly string[]).includes(rail) ? rail as BillingRail : 'stripe',
    trialEndsAtMs: toMillis(data.trialEndsAt),
    currentPeriodStartMs: toMillis(data.currentPeriodStart),
    currentPeriodEndMs: toMillis(data.currentPeriodEnd),
    cancelAtPeriodEnd: data.cancelAtPeriodEnd === true,
    pendingPlanId: isPlanId(data.pendingPlanId) ? data.pendingPlanId : null,
    houseQuota: asNullableInt(data.houseQuota),
    seatQuota: asNullableInt(data.seatQuota),
    overrides: normalizeOverrides(data.overrides),
    exempt: data.exempt === true,
    extraHouses: Math.max(0, asInt(data.extraHouses)),
    promoId: asText(data.promoId),
    promoCode: asText(data.promoCode),
    promoRemainingMonths: Object.prototype.hasOwnProperty.call(data, 'promoRemainingMonths')
      ? asNullableInt(data.promoRemainingMonths)
      : null,
    stripeSubscriptionId: asText(data.stripeSubscriptionId),
    pastDueSinceMs: toMillis(data.pastDueSince),
    amountDueMinor: Math.max(0, asInt(data.amountDueMinor)),
    attemptCount: Math.max(0, asInt(data.attemptCount)),
    nextRetryAtMs: toMillis(data.nextRetryAt),
    lastDunningNotifiedAtMs: toMillis(data.lastDunningNotifiedAt),
    lastRenewalNotifiedAtMs: toMillis(data.lastRenewalNotifiedAt),
    lastProviderEventAtMs: toMillis(data.lastProviderEventAt),
    archived: data.archived === true,
    archivedAtMs: toMillis(data.archivedAt),
    archivedBy: asText(data.archivedBy),
    // Missing field means legacy rows already in use — treat as complete.
    billingSetupComplete: data.billingSetupComplete !== false,
    updatedAtMs: toMillis(data.updatedAt),
  };
}

export function normalizeBillingConfig(raw: unknown): BillingPlatformConfig {
  const data = asRecord(raw);
  const prices = asRecord(data.prices);
  const dunning = asRecord(data.dunning);
  const accounts = asRecord(data.manualAccounts);
  const mode = String(data.verificationHoldMode ?? '');
  const resolved: Partial<Record<PlanId, number>> = {};
  for (const key of Object.keys(prices)) {
    if (isPlanId(key)) {
      const value = asNullableInt(prices[key]);
      if (value != null && value >= 0) resolved[key] = value;
    }
  }
  return {
    enforcement: data.enforcement === true,
    prices: resolved,
    extraHousePriceMinor: asNullableInt(data.extraHousePriceMinor),
    trialDays: Math.max(0, asInt(data.trialDays, TRIAL_DAYS)),
    platformFeeBps: Math.max(0, asInt(data.platformFeeBps, PLATFORM_FEE_BPS)),
    dunning: {
      graceDays: Math.max(0, asInt(dunning.graceDays, DEFAULT_DUNNING_POLICY.graceDays)),
      suspendDays: Math.max(1, asInt(dunning.suspendDays, DEFAULT_DUNNING_POLICY.suspendDays)),
    },
    verificationHoldMode: (VERIFICATION_HOLD_MODES as readonly string[]).includes(mode)
      ? mode as VerificationHoldMode
      : DEFAULT_BILLING_CONFIG.verificationHoldMode,
    verificationHoldFixedMinor: Math.max(0, asInt(
      data.verificationHoldFixedMinor,
      DEFAULT_BILLING_CONFIG.verificationHoldFixedMinor,
    )),
    manualAccounts: {
      zelle: asText(accounts.zelle),
      venmo: asText(accounts.venmo),
      transfer: asText(accounts.transfer),
    },
  };
}

/** Everything `resolveEntitlements` needs from a stored subscription. */
export function entitlementSourceOf(sub: SubscriptionDoc): EntitlementSource {
  return {
    planId: sub.planId,
    status: sub.status,
    houseQuota: sub.houseQuota,
    seatQuota: sub.seatQuota,
    overrides: sub.overrides,
    exempt: sub.exempt,
  };
}

/** Monthly price for a plan honouring god overrides from `platform/billing`. */
export function configuredPlanPriceMinor(
  config: BillingPlatformConfig,
  planId: PlanId,
  extraHouses = 0,
): number {
  const base = config.prices[planId] ?? PLAN_CATALOG[planId].priceMinor;
  const extraPrice = config.extraHousePriceMinor ?? PLAN_CATALOG[planId].extraHousePriceMinor;
  return base + Math.max(0, Math.trunc(extraHouses)) * extraPrice;
}

/** Amount to authorize and immediately release when verifying a card. */
export function verificationHoldMinor(
  config: BillingPlatformConfig,
  planId: PlanId,
  extraHouses = 0,
): number {
  if (config.verificationHoldMode === 'zero') return 0;
  if (config.verificationHoldMode === 'fixed') return config.verificationHoldFixedMinor;
  return configuredPlanPriceMinor(config, planId, extraHouses);
}
